import { LockKeyhole, ShieldCheck, ShoppingCart } from 'lucide-react';
import { NavLink, Outlet } from 'react-router';
import { useAdminSession } from '../api/admin-queries.js';
import { useCart } from '../cart/cart-context.js';

const NAVIGATION = [
  { to: '/', label: 'Catálogo' },
  { to: '/cart', label: 'Carro' },
  { to: '/trazabilidad', label: 'Trazabilidad' },
  { to: '/recalls', label: 'Retiros de lote' },
] as const;

function navLinkClass({ isActive }: { isActive: boolean }): string {
  const base =
    'inline-flex min-h-11 items-center rounded-control px-3 font-medium transition-colors duration-150';
  return isActive ? `${base} bg-primary text-on-primary` : `${base} text-foreground hover:bg-muted`;
}

/** Set apart from the shopping links: outlined while logged out, filled once inside. */
function AdminAccessLink() {
  const session = useAdminSession();
  const loggedIn = session.data !== undefined && session.data !== null;
  const Icon = loggedIn ? ShieldCheck : LockKeyhole;
  return (
    <NavLink
      to="/admin"
      className={({ isActive }) => {
        const base =
          'inline-flex min-h-11 items-center gap-2 rounded-control border-2 border-accent px-3 font-medium transition-colors duration-150';
        return isActive || loggedIn
          ? `${base} bg-accent text-on-accent hover:opacity-90`
          : `${base} text-accent hover:bg-accent/10`;
      }}
    >
      <Icon aria-hidden="true" className="size-4" />
      {loggedIn ? 'Panel admin' : 'Acceso administrador'}
    </NavLink>
  );
}

export function AppLayout() {
  const { itemCount } = useCart();
  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:bg-card focus:p-2"
      >
        Saltar al contenido
      </a>
      <header className="border-b border-border bg-card">
        <nav
          aria-label="Principal"
          className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-3"
        >
          <NavLink to="/" className="font-heading text-xl font-bold text-primary">
            Farma Central · G5
          </NavLink>
          <ul className="flex flex-wrap gap-2">
            {NAVIGATION.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} end className={navLinkClass}>
                  {item.to === '/cart' && (
                    <ShoppingCart aria-hidden="true" className="mr-1 size-4" />
                  )}
                  {item.label}
                  {item.to === '/cart' && itemCount > 0 && (
                    <span
                      className="ml-1.5 rounded-full bg-accent px-2 text-sm text-on-accent"
                      aria-label={`${String(itemCount)} kits`}
                    >
                      {itemCount}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
            <li className="flex items-center gap-2 sm:ml-2 sm:border-l sm:border-border sm:pl-4">
              <AdminAccessLink />
            </li>
          </ul>
        </nav>
      </header>
      <main id="main" className="mx-auto w-full max-w-6xl flex-1 px-4 py-8">
        <Outlet />
      </main>
      <footer className="border-t border-border bg-card py-4 text-center text-sm text-muted-foreground">
        Distribuidora farmacéutica · Grupo 5 · IIC3103 Taller de Integración
      </footer>
    </div>
  );
}
